import type { Review, ReviewsSummary } from '@/types/reviews';
import { HIGHLIGHTED_REVIEW_IDS, HIGHLIGHT_DISPLAY_PRIORITIES } from '@/data/reviewsHighlights';
import { getProductByShopifyId } from '@/data/productsMapping';

type RawObj = Record<string, unknown>;

function str(v: unknown, fallback = ''): string {
  return v != null ? String(v) : fallback;
}
function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return isNaN(n) ? fallback : n;
}
function bool(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}

export function mapKonfidencyReview(raw: RawObj, type: 'store' | 'product'): Review {
  const id = str(raw.id ?? raw._id ?? raw.reviewId ?? raw.review_id ?? '');
  const rating = num(raw.rating ?? raw.nota ?? raw.score ?? 5);
  const body = str(
    raw.body ?? raw.comment ?? raw.comentario ?? raw.text ?? raw.texto ?? raw.review ?? '',
    'Avaliação positiva'
  );
  const authorName = str(
    raw.authorName ?? raw.author ?? raw.nome ?? raw.name ?? raw.reviewer ?? '',
    'Cliente'
  );
  const verified = bool(raw.verified ?? raw.verificado ?? raw.isVerified ?? false);
  const createdAt = str(
    raw.created ?? raw.createdAt ?? raw.created_at ?? raw.date ?? raw.data ?? raw.publishedAt ?? '',
    new Date().toISOString()
  );
  const rawProductId = str(raw.productId ?? raw.product_id ?? raw.sku ?? raw.itemId ?? '');
  const productEntry = rawProductId ? getProductByShopifyId(rawProductId) : undefined;
  const productHandle = str(raw.productHandle ?? raw.handle ?? raw.slug ?? productEntry?.handle ?? '');
  const productName = str(raw.productName ?? raw.produto ?? raw.productTitle ?? productEntry?.name ?? '');

  return {
    id,
    source: 'konfidency',
    type,
    rating,
    title: str(raw.title ?? raw.titulo ?? ''),
    body,
    authorName,
    verified,
    createdAt,
    productId: type === 'product' ? rawProductId : undefined,
    productHandle: type === 'product' ? productHandle : undefined,
    productName: type === 'product' ? productName : undefined,
    tags: Array.isArray(raw.tags) ? raw.tags.map(String) : [],
    isHighlighted: HIGHLIGHTED_REVIEW_IDS.includes(id),
    displayPriority: HIGHLIGHT_DISPLAY_PRIORITIES[id] ?? 999,
    placementScope: ['global'],
  };
}

export function mapKonfidencyStoreSummary(raw: unknown): ReviewsSummary {
  // The API returns an array: [{ _id, count, avgRating, ... }]
  const obj: RawObj = Array.isArray(raw) ? (raw[0] as RawObj ?? {}) : (raw as RawObj ?? {});

  const dist = (obj.distribution ?? obj.ratingDistribution ?? {}) as Record<string, unknown>;
  return {
    averageRating: num(obj.avgRating ?? obj.averageRating ?? obj.media ?? obj.average ?? obj.avg ?? 0),
    totalReviews: num(obj.count ?? obj.totalReviews ?? obj.total ?? obj.totalCount ?? 0),
    distribution: {
      5: num(dist[5] ?? dist['5'] ?? 0),
      4: num(dist[4] ?? dist['4'] ?? 0),
      3: num(dist[3] ?? dist['3'] ?? 0),
      2: num(dist[2] ?? dist['2'] ?? 0),
      1: num(dist[1] ?? dist['1'] ?? 0),
    },
  };
}

export function extractReviewsArray(raw: unknown): RawObj[] {
  if (Array.isArray(raw)) return raw as RawObj[];

  if (raw && typeof raw === 'object') {
    const obj = raw as RawObj;

    // Product reviews endpoint returns: { reviews: [{ _id: sku, reviews: [...actual...] }] }
    if (Array.isArray(obj['reviews'])) {
      const outer = obj['reviews'] as RawObj[];
      if (
        outer.length > 0 &&
        typeof outer[0] === 'object' &&
        Array.isArray((outer[0] as RawObj)['reviews'])
      ) {
        return (outer[0] as RawObj)['reviews'] as RawObj[];
      }
      return outer;
    }

    // Store reviews endpoint returns: { results: [...], total: N }
    const candidates = ['results', 'data', 'items', 'avaliacoes'];
    for (const key of candidates) {
      if (Array.isArray(obj[key])) return obj[key] as RawObj[];
    }
  }
  return [];
}

export function extractTotal(raw: unknown, fallback: number): number {
  if (!raw || typeof raw !== 'object') return fallback;
  const obj = raw as RawObj;

  // Product reviews: total is inside reviews[0].reviewCount
  if (Array.isArray(obj['reviews'])) {
    const outer = obj['reviews'] as RawObj[];
    if (outer.length > 0 && (outer[0] as RawObj)['reviewCount'] != null) {
      return Number((outer[0] as RawObj)['reviewCount']);
    }
  }

  const v = obj.total ?? obj.totalCount ?? obj.count ?? obj.totalReviews;
  return v != null ? Number(v) : fallback;
}
